/**
 * Admin Withdrawals Contracts
 *
 * DATABASE-FIRST: All Zod schemas match PostgreSQL exactly
 * Source: MCP postgres queries on 2026-01-02
 *
 * Endpoints covered:
 * - GET /api/admin/withdrawals - List withdrawals with stats
 * - POST /api/admin/withdrawals/:id/resend - Resend to workers
 */
import { z } from 'zod';
import { withdrawalStatusSchema, bigintString } from '../common.js';
// =============================================================================
// ADMIN WITHDRAWAL SUMMARY
// Used in list view: GET /api/admin/withdrawals
// =============================================================================
/**
 * Individual withdrawal in admin list view
 * Maps from withdrawals table + users join + payment_services join
 *
 * DB columns: reference_number (PK), user_telegram_id, original_amount, original_bonus,
 * amount, status, completed_at, assigned_to, card_number, provider_hash, is_manual,
 * resend_used, created_at
 */
export const adminWithdrawalSummarySchema = z.object({
    /** Reference number used as ID (PK: reference_number) */
    id: z.string(),
    /** Reference number: WTH-YYYYMMDD-NNNNN */
    referenceNumber: z.string(),
    /** User's Telegram ID (bigint as string) */
    telegramId: bigintString,
    /** User's Telegram username (nullable) */
    username: z.string().nullable(),
    /** User's first name (nullable) */
    firstName: z.string().nullable(),
    /** Final withdrawal amount (after bonus deduction) */
    amount: z.number().int(),
    /** Original requested amount before bonus deduction */
    originalAmount: z.number().int(),
    /** Bonus amount deducted (nullable if no bonus) */
    bonusDeducted: z.number().int().nullable(),
    /** Card number for payout (nullable if not provided) */
    cardNumber: z.string().nullable(),
    /** Withdrawal status: pending | paid | completed | declined */
    status: withdrawalStatusSchema,
    /** Payment provider display name (nullable if not assigned) */
    paymentMethod: z.string().nullable(),
    /** Whether completed manually (not via cascade) */
    isManual: z.boolean(),
    /** Staff identifier assigned to process (nullable) */
    assignedTo: z.string().nullable(),
    /** ISO timestamp when created */
    createdAt: z.string(),
    /** ISO timestamp when completed (nullable if not completed) */
    completedAt: z.string().nullable(),
    /** Whether this withdrawal has already been resent once */
    resendUsed: z.boolean(),
    /** Photo URL if proof was uploaded (nullable) */
    photoUrl: z.string().nullable(),
});
// =============================================================================
// WITHDRAWALS STATS
// =============================================================================
/**
 * Stats for a single status category
 */
export const withdrawalStatsEntrySchema = z.object({
    count: z.number().int(),
    total: z.number().int(),
});
/**
 * All stats grouped by status
 */
export const withdrawalStatsSchema = z.object({
    pending: withdrawalStatsEntrySchema,
    completed: withdrawalStatsEntrySchema,
    declined: withdrawalStatsEntrySchema,
});
// =============================================================================
// WITHDRAWALS LIST RESPONSE
// GET /api/admin/withdrawals
// =============================================================================
/**
 * Response shape for GET /api/admin/withdrawals
 */
export const withdrawalsListResponseSchema = z.object({
    withdrawals: z.array(adminWithdrawalSummarySchema),
    stats: withdrawalStatsSchema,
    /** Available payment methods (from providers in use) */
    availablePaymentMethods: z.array(z.string()),
});
// =============================================================================
// RESEND WITHDRAWAL REQUEST/RESPONSE
// POST /api/admin/withdrawals/:id/resend
// =============================================================================
/**
 * Request body for resend withdrawal
 */
export const resendWithdrawalRequestSchema = z.object({
    /** Admin note explaining why resending (min 15 chars) */
    adminNote: z.string().min(15, 'Admin note is required (minimum 15 characters)'),
});
/**
 * Response shape for resend withdrawal
 */
export const resendWithdrawalResponseSchema = z.object({
    message: z.string(),
    referenceNumber: z.string(),
    previousStatus: withdrawalStatusSchema,
    newStatus: z.literal('pending'),
});
// =============================================================================
// QUERY PARAMS (for list endpoint)
// =============================================================================
export const withdrawalsListQuerySchema = z.object({
    /** Max results to return */
    limit: z.coerce.number().int().positive().optional().default(500),
    /** Search by reference, telegram_id, username, card_number, amount */
    search: z.string().optional().default(''),
});
// =============================================================================
// EDIT WITHDRAWAL REQUEST/RESPONSE
// PUT /api/admin/withdrawals/:id/edit
// =============================================================================
/**
 * Request body for editing withdrawal financials
 * At least one field must be provided
 */
export const editWithdrawalRequestSchema = z.object({
    /** USDT amount (optional) */
    usdtAmount: z.number().positive().optional(),
    /** Exchange rate (optional) */
    exchangeRate: z.number().positive().optional(),
    /** Commission percentage (optional) */
    commission: z.number().min(0).optional(),
    /** Admin note explaining the edit (optional) */
    note: z.string().optional(),
}).refine((data) => data.usdtAmount !== undefined || data.exchangeRate !== undefined || data.commission !== undefined, { message: 'At least one of usdtAmount, exchangeRate, or commission must be provided' });
/**
 * Response shape for edit withdrawal
 * Uses camelCase consistently (matches API conventions)
 */
export const editWithdrawalResponseSchema = z.object({
    message: z.string(),
    referenceNumber: z.string(),
    previousValues: z.object({
        usdtAmount: z.number().nullable(),
        exchangeRate: z.number().nullable(),
        commission: z.number().nullable(),
    }),
    newValues: z.object({
        usdtAmount: z.number().optional(),
        exchangeRate: z.number().optional(),
        commission: z.number().optional(),
    }),
});
// =============================================================================
// WITHDRAWAL DETAIL
// GET /api/admin/withdrawals/:id
// =============================================================================
/**
 * Withdrawal log entry (from withdrawal_logs table)
 */
export const withdrawalLogEntrySchema = z.object({
    id: z.number().int(),
    logType: z.string(),
    providerHash: z.string().nullable(),
    providerTransactionId: z.string().nullable(),
    photoUrl: z.string().nullable(),
    staffId: z.string().nullable(),
    note: z.string().nullable(),
    previousValues: z.record(z.unknown()).nullable(),
    newValues: z.record(z.unknown()).nullable(),
    result: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * Full withdrawal detail for admin view
 * Includes main withdrawal data + user info + logs
 */
export const adminWithdrawalDetailSchema = z.object({
    // Core withdrawal data
    referenceNumber: z.string(),
    telegramId: bigintString,
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    status: withdrawalStatusSchema,
    createdAt: z.string(),
    completedAt: z.string().nullable(),
    // Amount info
    originalAmount: z.number().int(),
    originalBonus: z.number().int().nullable(),
    amount: z.number().int(),
    cardNumber: z.string().nullable(),
    // Provider info
    providerHash: z.string().nullable(),
    providerTransactionId: z.string().nullable(),
    paymentMethod: z.string().nullable(),
    isManual: z.boolean(),
    photoUrl: z.string().nullable(),
    // Financial info
    usdtAmount: z.number().nullable(),
    exchangeRate: z.number().nullable(),
    commission: z.number().nullable(),
    // Flags
    assignedTo: z.string().nullable(),
    resendUsed: z.boolean(),
    // Logs for history
    logs: z.array(withdrawalLogEntrySchema),
});
/**
 * Response shape for GET /api/admin/withdrawals/:id
 */
export const adminWithdrawalDetailResponseSchema = adminWithdrawalDetailSchema;
//# sourceMappingURL=withdrawals.js.map